import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { EditableText } from "@/components/report/parts/EditableText";
import { Skeleton } from "@/components/ui/skeleton";

interface AiSummaryProps {
  aiHeadline: string | null;
  aiSummary: string | null;
  headlineStale: boolean;
  summaryStale: boolean;
  headlineExpanded: boolean;
  summaryExpanded: boolean;
  setHeadlineExpanded: (newVal: boolean) => void;
  setSummaryExpanded: (newVal: boolean) => void;
  refreshAiField: (field: string) => void;
  isSummaryLoading: boolean;
  isHeadlineLoading?: boolean;
  onTitleChange: (newTitle: string) => void;
  onSummaryChange: (newSummary: string) => void;
  showSummary?: boolean;
  editable: boolean;
}

export const AiSummaryV2: React.FC<AiSummaryProps> = ({
  aiHeadline,
  aiSummary,
  headlineStale,
  summaryStale,
  headlineExpanded,
  summaryExpanded,
  setHeadlineExpanded,
  setSummaryExpanded,
  editable,
  isSummaryLoading,
  isHeadlineLoading,
  refreshAiField,
  onTitleChange,
  onSummaryChange,
  showSummary = true,
}) => {
  const handleTitleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newTitle = e.target.value;
    onTitleChange(newTitle);
  };
  const handleSummaryChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newSummary = e.target.value;
    onSummaryChange(newSummary);
  };

  const headlineOrSkeleton = isHeadlineLoading ? (
    <Skeleton className="pl-3 h-9 w-1/2" />
  ) : (
    <EditableText
      text={aiHeadline as string}
      onChange={handleTitleChange}
      canEdit={editable}
      className={`${
        headlineStale ? "text-gray-300" : "text-gray-700"
      } text-xl font-semibold h-9 mr-10 min-h-11`}
      component="textarea"
    />
  );

  const summaryOrSkeleton = isSummaryLoading ? (
    <div className="py-4">
      <Skeleton className="w-full h-4 mb-2" />
      <Skeleton className="w-3/4 h-4 mb-2" />
      <Skeleton className="w-1/2 h-4 mb-10" />

      <Skeleton className="w-full h-4 mb-2" />
      <Skeleton className="w-3/4 h-4 mb-2" />
      <Skeleton className="w-1/2 h-4" />
    </div>
  ) : (
    <EditableText
      text={aiSummary as string}
      onChange={handleSummaryChange}
      canEdit={editable}
      className={`${summaryStale ? "text-gray-300" : "text-gray-700"} text-sm grow min-h-[250px]`}
      component="textarea"
    />
  );

  return (
    <>
      <div className="px-4 py-2">
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm grow text-gray-500">
            {headlineExpanded && editable && headlineStale && (
              <div className="text-xs mb-2">
                Headline is outdated.&nbsp;
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isHeadlineLoading}
                  onClick={refreshAiField.bind(null, "headline")}
                  className="ml-2 text-primary text-xs border-primary hover:text-white hover:bg-primary/90"
                >
                  {isHeadlineLoading ? "Updating..." : "Update Headline"}
                  {isHeadlineLoading && (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  )}
                </Button>
              </div>
            )}
            {headlineExpanded && aiHeadline && headlineOrSkeleton}
          </div>
          <button
            onClick={() => setHeadlineExpanded(!headlineExpanded)}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {!headlineExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      {showSummary && (
        <div className="px-4 py-2 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm grow text-gray-500">
              {!summaryExpanded && "Summary"}
              {summaryExpanded && editable && summaryStale && (
                <div className="text-xs mb-2">
                  Summary information is outdated.&nbsp;
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isSummaryLoading}
                    onClick={refreshAiField.bind(null, "summary")}
                    className="ml-2 text-primary text-xs border-primary hover:text-white hover:bg-primary/90"
                  >
                    {isSummaryLoading ? "Updating..." : "Update Summary"}
                    {isSummaryLoading && (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    )}
                  </Button>
                </div>
              )}
              {summaryExpanded && (
                <div className="">{aiSummary && summaryOrSkeleton}</div>
              )}
            </div>
            <button
              onClick={() => setSummaryExpanded(!summaryExpanded)}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 self-start"
            >
              {!summaryExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
